import React, { useState, useEffect, useCallback } from 'react';
import { X, MapPin, Navigation, Loader2, Search, Edit3 } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from '../ui/simple-toaster';

const STATE_ABBREVIATIONS = {
  'new south wales': 'NSW',
  'victoria': 'VIC',
  'queensland': 'QLD',
  'south australia': 'SA',
  'western australia': 'WA',
  'tasmania': 'TAS',
  'northern territory': 'NT',
  'australian capital territory': 'ACT'
};

// Extract suburb and state abbreviation from full address
const extractSuburbAndState = (fullAddress) => {
  if (!fullAddress) return '';
  
  const parts = fullAddress.split(',').map(p => p.trim());
  if (parts.length < 2) return parts[0] || '';
  
  let suburb = '';
  let state = '';
  
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const partLower = part.toLowerCase();
    
    for (const [fullName, abbrev] of Object.entries(STATE_ABBREVIATIONS)) {
      if (partLower.includes(fullName)) {
        state = abbrev;
        const stateIndex = partLower.indexOf(fullName);
        if (stateIndex > 0) {
          suburb = part.substring(0, stateIndex).trim();
        } else if (i > 0) {
          suburb = parts[i - 1].replace(/\d{4}/, '').trim();
        }
        break;
      }
    }
    
    if (!state) {
      const abbrevMatch = part.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i);
      if (abbrevMatch) {
        state = abbrevMatch[1].toUpperCase();
        const beforeState = part.substring(0, part.toUpperCase().indexOf(state)).trim();
        if (beforeState) {
          suburb = beforeState.replace(/\s*\d{4}\s*$/, '').trim();
        } else if (i > 0) {
          suburb = parts[i - 1].replace(/\d{4}/, '').trim();
        }
      }
    }
    
    if (state && suburb) break;
  }
  
  if (!suburb && parts.length >= 2 && state) {
    const secondPart = parts[1];
    suburb = secondPart
      .replace(/new south wales|victoria|queensland|south australia|western australia|tasmania|northern territory|australian capital territory/gi, '')
      .replace(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/gi, '')
      .replace(/\d{4}/, '')
      .trim()
      .replace(/,+$/, '');
  }
  
  if (!suburb && parts.length >= 1) {
    suburb = parts[0];
  }
  
  suburb = suburb.replace(/^,+|,+$/g, '').trim();
  
  return state ? `${suburb}, ${state}` : suburb;
};

/**
 * RestLocationModal - Captures location when starting REST
 * Stores TWO address formats:
 * 1. FULL ADDRESS: Street number, street name, suburb, state, postcode
 * 2. SUBURB/STATE: Short format like "Parramatta, NSW"
 */
const RestLocationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  driverId
}) => {
  const [loading, setLoading] = useState(true);
  const [gpsStatus, setGpsStatus] = useState('checking'); // checking, success, failed, denied
  const [location, setLocation] = useState({
    fullAddress: '',
    suburbState: '',
    lat: null,
    lng: null,
    source: 'GPS'
  });
  const [manualAddress, setManualAddress] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Manual entry mode
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualSuburb, setManualSuburb] = useState('');
  const [manualState, setManualState] = useState('');

  // Australian states for dropdown
  const STATES = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'SA', label: 'South Australia' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'NT', label: 'Northern Territory' },
    { value: 'ACT', label: 'Australian Capital Territory' }
  ];

  // Australian state name to abbreviation mapping
  // Get GPS location
  const getGPSLocation = useCallback(() => {
    setLoading(true);
    setGpsStatus('checking');

    if (!navigator.geolocation) {
      setGpsStatus('failed');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation(prev => ({ ...prev, lat: latitude, lng: longitude }));
        
        // Reverse geocode to get address
        try {
          const result = await api(`/journey/reverse-geocode?lat=${latitude}&lng=${longitude}`);
          if (result.address) {
            const suburbState = extractSuburbAndState(result.address);
            setLocation({
              fullAddress: result.address,
              suburbState: suburbState,
              lat: latitude,
              lng: longitude,
              source: 'GPS'
            });
            setGpsStatus('success');
          } else {
            setGpsStatus('failed');
          }
        } catch (err) {
          console.error('Reverse geocode failed:', err);
          setGpsStatus('failed');
        }
        setLoading(false);
      },
      (error) => {
        console.error('GPS error:', error);
        if (error.code === error.PERMISSION_DENIED) {
          setGpsStatus('denied');
        } else {
          setGpsStatus('failed');
        }
        setLoading(false);
      },
      { 
        enableHighAccuracy: true, 
        timeout: 10000, 
        maximumAge: 60000 
      }
    );
  }, []);

  // Try to get GPS location on mount
  useEffect(() => {
    if (isOpen) {
      getGPSLocation();
      // Reset manual entry state
      setShowManualEntry(false);
      setManualSuburb('');
      setManualState('');
    }
  }, [isOpen, getGPSLocation]);

  // Search for address suggestions
  const searchAddress = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setSearchLoading(true);
    try {
      const result = await api(`/journey/autocomplete?query=${encodeURIComponent(query)}`);
      setSuggestions(result.suggestions || []);
    } catch (err) {
      console.error('Address search failed:', err);
    }
    setSearchLoading(false);
  };

  // Select an address from suggestions
  const selectAddress = async (address) => {
    setManualAddress(address);
    setSuggestions([]);
    
    try {
      const result = await api(`/journey/geocode?address=${encodeURIComponent(address)}`);
      if (result.coordinates) {
        const suburbState = extractSuburbAndState(address);
        setLocation({
          fullAddress: address,
          suburbState: suburbState,
          lat: result.coordinates.lat,
          lng: result.coordinates.lng,
          source: 'MANUAL'
        });
      }
    } catch (err) {
      const suburbState = extractSuburbAndState(address);
      setLocation({
        fullAddress: address,
        suburbState: suburbState,
        lat: null,
        lng: null,
        source: 'MANUAL'
      });
    }
  };

  const handleConfirm = () => {
    // Check if we have location data
    if (!location.fullAddress && !manualAddress && !showManualEntry) {
      toast.error('Please confirm or enter your location');
      return;
    }

    // If manual entry mode, build from suburb/state fields
    if (showManualEntry) {
      if (!manualSuburb.trim()) {
        toast.error('Please enter suburb/town');
        return;
      }
      if (!manualState) {
        toast.error('Please select state/territory');
        return;
      }
      
      const suburbState = `${manualSuburb.trim()}, ${manualState}`;
      const finalLocation = {
        address: suburbState,  // Use suburb/state as address too
        suburb: suburbState,
        lat: null,
        lng: null,
        source: 'MANUAL'
      };
      
      console.log('[RestLocationModal] Confirming manual entry:', finalLocation);
      onConfirm(finalLocation);
      return;
    }

    // Build final location with BOTH full address and suburb/state
    const fullAddr = location.fullAddress || manualAddress;
    const suburbState = location.suburbState || extractSuburbAndState(manualAddress);
    
    const finalLocation = {
      address: fullAddr,
      suburb: suburbState,
      lat: location.lat,
      lng: location.lng,
      source: location.source || 'MANUAL'
    };

    console.log('[RestLocationModal] Confirming location:', finalLocation);
    onConfirm(finalLocation);
  };

  const handleManualEntry = () => {
    setShowManualEntry(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Rest Location</h3>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-slate-700 rounded-lg"
            data-testid="rest-location-close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Manual Entry Form */}
          {showManualEntry ? (
            <div className="space-y-4">
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <p className="text-blue-400 text-sm font-medium">Manual Location Entry</p>
              </div>
              
              {/* Suburb/Town Input */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Suburb / Town</label>
                <input
                  type="text"
                  value={manualSuburb}
                  onChange={(e) => setManualSuburb(e.target.value)}
                  placeholder="e.g., Parramatta"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  data-testid="manual-suburb-input"
                />
              </div>
              
              {/* State/Territory Dropdown */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">State / Territory</label>
                <select
                  value={manualState}
                  onChange={(e) => setManualState(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-blue-500"
                  data-testid="manual-state-select"
                >
                  <option value="">Select state...</option>
                  {STATES.map(s => (
                    <option key={s.value} value={s.value}>{s.label} ({s.value})</option>
                  ))}
                </select>
              </div>
              
              {/* Back button */}
              <button
                onClick={() => setShowManualEntry(false)}
                className="text-sm text-slate-400 hover:text-white transition"
              >
                ← Back to location search
              </button>
            </div>
          ) : (
            <>
              {/* GPS Status */}
              {loading ? (
                <div className="flex items-center justify-center py-8 gap-3">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  <span className="text-slate-300">Getting your location...</span>
                </div>
              ) : gpsStatus === 'success' ? (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Navigation className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-emerald-400 font-medium">Location Found</p>
                      <p className="text-white mt-2 text-sm">
                        <span className="text-slate-400">Full: </span>
                        {location.fullAddress}
                      </p>
                      {location.suburbState && (
                        <p className="text-white mt-1 text-sm">
                          <span className="text-slate-400">Short: </span>
                          <span className="text-emerald-400 font-medium">{location.suburbState}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                    <p className="text-amber-400 text-sm">
                      {gpsStatus === 'denied' 
                        ? 'Location permission denied.'
                        : 'Could not get GPS location.'}
                    </p>
                    <p className="text-slate-400 text-xs mt-1">
                      Search for an address or use manual entry below.
                    </p>
                  </div>

                  {/* Address Search Input */}
                  <div className="relative">
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-700 border border-slate-600 rounded-xl">
                      <Search className="w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={manualAddress}
                        onChange={(e) => {
                          setManualAddress(e.target.value);
                          searchAddress(e.target.value);
                        }}
                        placeholder="Search address..."
                        className="flex-1 bg-transparent text-white outline-none text-sm"
                        data-testid="rest-location-input"
                      />
                      {searchLoading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                    </div>

                    {/* Suggestions Dropdown */}
                    {suggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 border border-slate-600 rounded-xl overflow-hidden z-10 max-h-48 overflow-y-auto">
                        {suggestions.map((suggestion) => (
                          <button
                            key={suggestion}
                            onClick={() => selectAddress(suggestion)}
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-slate-600 transition"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Retry GPS Button */}
              {!loading && gpsStatus !== 'success' && gpsStatus !== 'denied' && (
                <button
                  onClick={getGPSLocation}
                  className="w-full py-2 text-sm text-blue-400 hover:text-blue-300 transition"
                >
                  <Navigation className="w-4 h-4 inline mr-2" />
                  Try GPS Again
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex gap-3">
          {!showManualEntry && (
            <button
              onClick={handleManualEntry}
              className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl font-medium transition flex items-center justify-center gap-2"
              data-testid="rest-location-manual"
            >
              <Edit3 className="w-4 h-4" />
              Manual Entry
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`${showManualEntry ? 'flex-1' : 'flex-1'} py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition`}
            data-testid="rest-location-confirm"
          >
            Confirm Location
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestLocationModal;
